import { notification, Upload, UploadProps } from 'antd';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { BackIcon, PlusIcon } from '@/assets/icons';
import ButtonWrap from '@/components/ButtonWrap';
import IconWrap from '@/components/IconWrap';
import { useGlobal } from '@/hooks/useGlobal';
import ImportService from '@/services/import.service';
import { handleMessageError } from '@/util/handleMessageError';

import './style.scss';

interface IActionButton {
    title: string;
    url?: string;
    icon?: React.ReactNode;
    onClick?: () => void;
}
interface IProps {
    title?: string;
    storeName?: string;
    dateFilter?: string;
    actions?: IActionButton[];
    isBack?: boolean;
    isImport?: boolean;
    multipleStore?: boolean;
    api?: string;
    onHandleModal?: () => void;
}

export const HeaderLayout: React.FC<IProps> = ({
    title,
    actions,
    isImport = false,
    multipleStore = false,
    isBack = false,
    api,
    storeName,
    dateFilter,
    onHandleModal,
}) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { paramsNavigator, setParamsNavigator } = useGlobal();

    const queryClient = useQueryClient();

    const { mutate, isLoading } = useMutation((e: File) => ImportService.importCSV(e, api as string), {
        onSuccess: async (data: any) => {
            notification.success({
                message: t('SUCCESS'),
                description: `${t('SUCCESS_IMPORT', { name: 'CSV' })}`,
            });
            // fetch api
            if (api === 'plan/upload-single-file') {
                await queryClient.refetchQueries('plan', { active: true });
            }
        },
        onError: (error: any) => {
            handleMessageError(error, t);
        },
    });

    const props: UploadProps = {
        beforeUpload: (file) => {
            const isCSV =
                file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                file.type === 'application/vnd.ms-excel' ||
                file.type === 'text/csv';
            if (!isCSV) {
                notification.error({
                    message: t('ERROR'),
                    description: t('validateMessage:CSV_ERROR', { name: file.name }),
                });
            }
            return isCSV || Upload.LIST_IGNORE;
        },
    };

    const uploadImage = async (options: any) => {
        const { file } = options;
        mutate(file);
    };

    const onGoPage = (e: any) => {
        if (paramsNavigator.router) {
            navigate(paramsNavigator.router, {
                state: paramsNavigator.state,
            });
            return;
        }

        queryClient.clear();
        navigate(-1);
    };

    useEffect(() => {
        return () => {
            isBack &&
                setParamsNavigator({
                    router: '',
                    state: {},
                });
        };
    }, [isBack, setParamsNavigator]);

    return (
        <div className="c-header">
            <div className="c-header__title">
                {isBack && <IconWrap onClick={onGoPage} icon={<BackIcon />} />}
                <h1>
                    <span className="no-print">{title}</span>
                    <span className="print">{title?.split('/')[1] || ''}</span>
                    <span className="print">{storeName}</span>
                    <span className="print">{dateFilter}分</span>
                </h1>
            </div>
            <div className="c-header__action">
                {!multipleStore && isImport && (
                    <div className="importFile">
                        <Upload
                            {...props}
                            showUploadList={false}
                            accept=".csv,.xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                            customRequest={uploadImage}
                        >
                            <ButtonWrap
                                isLoading={isLoading}
                                type="primary"
                                text={t('IMPORT_CSV')}
                                icon={<IconWrap icon={<PlusIcon />} />}
                            />
                        </Upload>
                    </div>
                )}
                {multipleStore && isImport && (
                    <div className="importFile">
                        <ButtonWrap
                            isLoading={isLoading}
                            type="primary"
                            onClick={onHandleModal}
                            text={t('IMPORT_CSV')}
                            icon={<IconWrap icon={<PlusIcon />} />}
                        />
                    </div>
                )}
                {actions?.length &&
                    actions.map(({ title, url, icon, onClick }, index) => (
                        <ButtonWrap
                            key={index}
                            icon={icon ? <IconWrap icon={icon} /> : null}
                            text={title} 
                            type="primary"
                            onClick={onClick || (() => {
                                url && navigate(url);
                            })}
                        />
                    ))}
            </div>
        </div>
    );
};
